/**
 * Licensing (ADR 0051, R-LC-1, R-LC-2): one entitlement catalogue every module declares
 * against, plans as data, and the shapes the hub gate, the control plane's Stripe billing and
 * the console share. Browser-safe: no Node built-ins, no runtime dependencies.
 *
 * This file is the contract. The pure checks (`effective`, `entitled`, `limitLeft`) and the hub
 * module `licensing` come with the licensing foundation; Stripe lives only on the control plane.
 * The published plans are Solo (free), Team and Enterprise; `local` is the Node hub's own plan
 * with every feature on and no limits (development, tests, `orgus up`).
 */
import type { Id, Ts, UiErrorShape } from '../index.ts';

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

export type EntitlementKind = 'feature' | 'limit';

export type EntitlementUnit = 'machines' | 'people' | 'projects' | 'days' | 'per-day' | 'per-month' | 'count';

/**
 * What happens to a feature's existing data when the plan stops including it. Nothing is ever
 * deleted: 'read-only' keeps it visible (export included) and refuses changes; 'hidden-kept'
 * stops the behaviour and keeps its settings for when the plan returns; 'n/a' for always-on
 * features and for limits.
 */
export type EntitlementOffBehaviour = 'read-only' | 'hidden-kept' | 'n/a';

export interface Entitlement {
  /** 'missions', 'rbac.scoped', 'limit.machines'. Limits start with 'limit.'. */
  key: string;
  kind: EntitlementKind;
  /** "Missions and templates". Sentence case. */
  title: string;
  /** One sentence for the upgrade card: what turning it on gives. */
  unlocks: string;
  /** Hub, control-plane and agent module names that need it ('core' for apps/hub/src/hub.ts). */
  modules: string[];
  /** For limits: what the number counts. */
  unit?: EntitlementUnit;
  offBehaviour: EntitlementOffBehaviour;
}

/**
 * Freezes the catalogue and the plans all the way down: they are shared by every request in a
 * long-running hub, so a module that changed one by mistake would change what every org gets.
 */
function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const v of Object.values(value as Record<string, unknown>)) deepFreeze(v);
  }
  return value;
}

const feature = (key: string, title: string, unlocks: string, modules: string[], offBehaviour: EntitlementOffBehaviour = 'read-only'): Entitlement => ({
  key,
  kind: 'feature',
  title,
  unlocks,
  modules,
  offBehaviour,
});
const limit = (key: string, title: string, unlocks: string, modules: string[], unit: EntitlementUnit): Entitlement => ({
  key,
  kind: 'limit',
  title,
  unlocks,
  modules,
  unit,
  offBehaviour: 'n/a',
});

/**
 * Every feature and limit a plan can include. The single list: modules declare against these
 * keys, the hub and control plane check them, the console and the public plan cards read them.
 */
export const ENTITLEMENTS: readonly Entitlement[] = deepFreeze([
  // Every plan includes (safety, trust and the onboarding path are never paywalled).
  feature('sessions', 'Sessions, machines and projects', 'Every Claude Code session, subagent and workflow live, on every connected machine.', ['core'], 'n/a'),
  feature('trust', 'Node trust', 'A key per machine, signed commands, ceilings nothing can raise, and an org-wide freeze.', ['trust'], 'n/a'),
  feature('audit', 'Audit log and export', 'A hash-chained audit log you can search and export.', ['audit-ops'], 'n/a'),
  feature('policy', 'Policy and approvals', 'Rules for what agents may do, and approvals from the console or your phone.', ['policy', 'approvals'], 'n/a'),
  feature('control', 'Session control', 'Control every session on a machine, with diffs and terminals.', ['control', 'session-control', 'diff', 'term'], 'n/a'),
  feature('budgets', 'Budgets with hard stops', 'Org and session budgets that stop work at the limit.', ['costs'], 'n/a'),
  feature('notify', 'Browser and phone notifications', 'Push notifications in the browser and on your phone.', ['notify'], 'n/a'),
  feature('signin', 'Sign in with GitHub, Google or Microsoft', 'One-click sign-in with an account you already have.', ['auth'], 'n/a'),
  feature('people', 'People, invites and roles', 'Invite people, give each a role, and issue scoped API tokens.', ['auth'], 'n/a'),
  feature('missions', 'Missions and templates', 'Start a mission from a template and follow it to done.', ['missions', 'templates'], 'n/a'),
  feature('scheduler', 'Scheduler and fleet', 'Place work on machines with capacity, and install and update the fleet.', ['scheduler', 'fleet'], 'n/a'),
  feature('views', 'Saved views and attachments', 'Save the views you use and attach files to approvals and missions.', ['views', 'attachments'], 'n/a'),
  feature('discipline', 'Discipline checks', 'Built-in rule packs, scores, ratchets and waivers for every repo.', ['discipline'], 'n/a'),
  feature('ai.api', 'AI API and MCP', 'Let agents read Orgus and act through the capability catalogue.', ['ai'], 'n/a'),
  feature('assistant', 'The assistant', 'Ask the assistant anything and let it run everyday changes for you.', ['assistant'], 'n/a'),
  feature('setup', 'Assistant-led setup', 'Orgus detects your stack and CI and proposes a setup you approve once.', ['setup'], 'n/a'),
  feature('agents.local', 'Every local coding agent', 'Claude Code, Codex and every ACP agent (Gemini CLI, Cursor, Copilot and more) with remote approvals.', ['core'], 'n/a'),
  feature('onboarding', 'Getting started', 'A guided path from sign-in to a running mission.', ['onboarding'], 'n/a'),

  // Team: collaboration and delivery.
  feature('budgets.scoped', 'Project and mission budgets', 'Budgets per project and per mission, with CSV export.', ['costs']),
  feature('finance', 'Finance', 'A profit and loss view of AI, seats, compute and CI, reconciled against invoices.', ['finance']),
  feature('notify.channels', 'Team channels', 'Notifications in Slack, Microsoft Teams, email and webhooks.', ['notify'], 'hidden-kept'),
  feature('teams', 'Teams and workspaces', 'Teams, workspaces, routing rules and on-call rotas.', ['teams']),
  feature('rbac.approval-policies', 'Environment approval policies', 'Require an approver who is not the author before a production deploy.', ['rbac', 'approvals']),
  feature('rbac.service-accounts', 'Service accounts and agent tokens', 'Least-privilege service accounts and agent tokens for CI and automation.', ['rbac', 'ai']),
  feature('backlog', 'Backlog and requirements', 'A backlog, requirements and AI jobs that turn them into missions.', ['missions', 'aijobs']),
  feature('landing', 'Landing queue and environments', 'Land changes through a queue that verifies them, and deploy to environments.', ['landing']),
  feature('ci.providers', 'CI/CD providers', 'Runs, logs, re-runs and deploy gates from GitHub Actions, GitLab CI, Azure Pipelines, Jenkins, CircleCI, Buildkite and any webhook.', ['ci']),
  feature('flows', 'Flows and comments', 'Hand work between people and agents, and discuss it in place.', ['flows', 'comments']),
  feature('secrets', 'Secrets leasing', 'Lease secrets to the sessions that need them, never stored in plain text.', ['secrets']),
  feature('standards.org', 'Org standards', 'Publish your own standards, pin them per project, enforce a floor and deliver them into every agent session.', ['discipline']),
  feature('tokens.sets', 'Design token sets', 'Import your design tokens from files and export them for CSS, Tailwind, iOS and Android.', ['design']),
  feature('assistant.autonomy', 'Assistant autonomy', 'Let the assistant run everything but critical changes, or work with full autonomy.', ['assistant'], 'hidden-kept'),
  feature('agents.cloud', 'Cloud agents', 'Run Devin, Cursor, Copilot, Jules and Codex cloud as sessions of your missions.', ['agents-cloud']),

  // Enterprise: identity, compliance and control at scale.
  feature('audit.siem', 'SIEM streaming', 'Stream the audit log to your SIEM as it is written.', ['audit-ops'], 'hidden-kept'),
  feature('audit.verify-kit', 'Offline audit verification', 'Verify the audit chain offline with the verification kit.', ['audit-ops'], 'hidden-kept'),
  feature('signin.enterprise', 'Enterprise sign-in', 'SAML, enforced single sign-on and domain capture.', ['auth'], 'hidden-kept'),
  feature('rbac.scoped', 'Scoped roles', 'Give a person admin in one workspace and viewer in another.', ['rbac']),
  feature('rbac.custom-roles', 'Custom roles', 'Compose your own roles from the permission catalogue.', ['rbac']),
  feature('rbac.idp-groups', 'Identity provider groups', 'Map GitHub teams and Microsoft Entra groups to roles.', ['rbac', 'auth']),
  feature('rbac.scim', 'Directory sync (SCIM)', 'Provision and remove people from Entra ID or Okta automatically.', ['scim']),
  feature('rbac.quorum', 'Approval quorums and separation of duties', 'Two-person rules for production and for granting admin.', ['rbac', 'approvals', 'landing']),
  feature('rbac.breakglass', 'Break-glass access', 'Time-boxed emergency access with a reason, notification and review.', ['rbac'], 'hidden-kept'),
  feature('rbac.access-review', 'Access review', 'Who holds what, through which binding, and grants unused for 90 days.', ['rbac'], 'hidden-kept'),
  feature('ci.relay', 'CI behind your firewall', 'Reach Jenkins, GitLab or Azure DevOps Server on a private network through a machine.', ['ci', 'ci-relay'], 'hidden-kept'),
  feature('fleet.rings', 'Update rings', 'Roll agent updates out to rings of machines in order.', ['fleet'], 'hidden-kept'),
  feature('secrets.cmk', 'Customer-managed keys', 'Wrap secrets with a key you hold.', ['secrets']),
  feature('standards.workspaces', 'Workspace standards', 'A standards layer per workspace between the org and its projects.', ['discipline']),
  feature('tokens.figma-api', 'Figma variables sync', 'Import variables straight from Figma (needs your Figma Enterprise plan).', ['design'], 'hidden-kept'),
  feature('design.gate', 'Generated design gate', 'Generate design rules from your tokens and component library and enforce them.', ['design', 'discipline']),
  feature('assistant.events', 'Event-driven assistant', 'Let the assistant act on events, not only when asked.', ['assistant'], 'hidden-kept'),
  feature('agents.a2a', 'A2A agents', "Run any agent that speaks A2A, such as your company's own agents.", ['agents-cloud']),

  // Limits.
  limit('limit.machines', 'Connected machines', 'Connect more machines.', ['core'], 'machines'),
  limit('limit.people', 'People', 'Invite more people.', ['auth'], 'people'),
  limit('limit.missions.monthly', 'Missions a month', 'Start more missions each month.', ['missions'], 'per-month'),
  limit('limit.audit.days', 'Audit log retention', 'Keep the audit log longer.', ['audit-ops'], 'days'),
  limit('limit.history.days', 'Session and cost history', 'Keep session and cost history longer.', ['core', 'costs'], 'days'),
]);

export function entitlementByKey(key: string): Entitlement | undefined {
  return ENTITLEMENTS.find((e) => e.key === key);
}

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export type PlanId = 'solo' | 'team' | 'enterprise' | 'local';
export const PLAN_IDS: readonly PlanId[] = ['solo', 'team', 'enterprise', 'local'];
/** The plans a customer can be on. 'local' is the Node hub's own. */
export const SOLD_PLAN_IDS: readonly Exclude<PlanId, 'local'>[] = ['solo', 'team', 'enterprise'];

export interface Plan {
  id: PlanId;
  /** "Solo", "Team", "Enterprise", "Local hub". */
  title: string;
  /** One line under the title. */
  summary: string;
  /** Feature keys the plan includes. */
  features: string[];
  /** Every limit key; null is unlimited. */
  limits: Record<string, number | null>;
  /** The Stripe price lookup key, for plans sold through Stripe. */
  priceLookupKey?: string;
}

/** "Every plan includes": safety, trust and the onboarding path are never paywalled. */
export const EVERY_PLAN_FEATURES: readonly string[] = deepFreeze([
  'sessions',
  'trust',
  'audit',
  'policy',
  'control',
  'budgets',
  'notify',
  'signin',
  'people',
  'missions',
  'scheduler',
  'views',
  'discipline',
  'ai.api',
  'assistant',
  'setup',
  'agents.local',
  'onboarding',
]);
const EVERY_PLAN = EVERY_PLAN_FEATURES;
const TEAM_ADDS = ['budgets.scoped', 'finance', 'notify.channels', 'teams', 'rbac.approval-policies', 'rbac.service-accounts', 'backlog', 'landing', 'ci.providers', 'flows', 'secrets', 'standards.org', 'tokens.sets', 'assistant.autonomy', 'agents.cloud'];
const ENTERPRISE_ADDS = [
  'audit.siem',
  'audit.verify-kit',
  'signin.enterprise',
  'rbac.scoped',
  'rbac.custom-roles',
  'rbac.idp-groups',
  'rbac.scim',
  'rbac.quorum',
  'rbac.breakglass',
  'rbac.access-review',
  'ci.relay',
  'fleet.rings',
  'secrets.cmk',
  'standards.workspaces',
  'tokens.figma-api',
  'design.gate',
  'assistant.events',
  'agents.a2a',
];
const UNLIMITED: Record<string, number | null> = Object.fromEntries(ENTITLEMENTS.filter((e) => e.kind === 'limit').map((e) => [e.key, null]));

/**
 * The default split (ADR 0051 point 4, owner to confirm). The control plane may override a
 * plan's contents per deployment from D1, so changing what Team includes is a data change.
 * Default numbers are settings: Solo's 20 missions a month and 7 days of audit and history.
 */
export const PLANS: Readonly<Record<PlanId, Plan>> = deepFreeze({
  solo: {
    id: 'solo',
    title: 'Solo',
    summary: 'Free for one person on one machine.',
    features: [...EVERY_PLAN],
    limits: { ...UNLIMITED, 'limit.machines': 1, 'limit.people': 1, 'limit.missions.monthly': 20, 'limit.audit.days': 7, 'limit.history.days': 7 },
  },
  team: {
    id: 'team',
    title: 'Team',
    summary: 'Per connected machine a month, unlimited people.',
    features: [...EVERY_PLAN, ...TEAM_ADDS],
    limits: { ...UNLIMITED, 'limit.audit.days': 365, 'limit.history.days': 365 },
    priceLookupKey: 'team_machine_monthly',
  },
  enterprise: {
    id: 'enterprise',
    title: 'Enterprise',
    summary: 'Identity, compliance and control at scale, billed yearly.',
    features: [...EVERY_PLAN, ...TEAM_ADDS, ...ENTERPRISE_ADDS],
    limits: { ...UNLIMITED },
    priceLookupKey: 'enterprise_yearly',
  },
  local: {
    id: 'local',
    title: 'Local hub',
    summary: 'Every feature on, no limits, no billing.',
    features: ENTITLEMENTS.filter((e) => e.kind === 'feature').map((e) => e.key),
    limits: { ...UNLIMITED },
  },
});

/** The environment variable that makes a local hub behave as a sold plan (tests, screenshots). */
export const PLAN_ENV = 'ORGUS_PLAN';

// ---------------------------------------------------------------------------
// An org's entitlements
// ---------------------------------------------------------------------------

export type EntitlementSource = 'stripe' | 'contract' | 'design-partner' | 'trial' | 'local' | 'licence';
/** 'grace': payment failed, plan kept for now. 'lapsed': back to Solo, data kept read-only. */
export type EntitlementStatus = 'active' | 'trialing' | 'grace' | 'lapsed';

export interface OrgEntitlements {
  orgId: Id;
  plan: PlanId;
  /** Per-org changes on top of the plan: a design partner, an enterprise contract, a trial. */
  overrides: { add?: string[]; remove?: string[]; limits?: Record<string, number | null> };
  source: EntitlementSource;
  status: EntitlementStatus;
  validUntil: Ts | null;
  /** A contract or invoice reference, for contract and design-partner sources. */
  reference?: string;
  /** Bumps on every change; the state stream re-sends on change. */
  rev: number;
}

/** What `effective(orgEnt)` returns: the features that are on, and every limit. */
export interface EffectiveEntitlements {
  plan: PlanId;
  status: EntitlementStatus;
  features: string[];
  limits: Record<string, number | null>;
  rev: number;
}

export type EntitlementRefusal = 'plan' | 'limit' | 'lapsed';

/** What `entitled(eff, key)` answers. */
export type EntitlementCheck =
  | { ok: true }
  | { ok: false; reason: EntitlementRefusal; key: string; needs: PlanId; title: string; unlocks: string };

/** The console's view of one key (`useEntitlement`). */
export type EntitlementUiState = 'on' | 'locked' | 'at-limit' | 'lapsed';

/**
 * A module's declaration (beside its `name`): which routes and actions need which key. Reads are
 * never gated unless a route says so; a locked feature's data stays visible.
 */
export interface ModuleEntitlements {
  /** The key the whole module needs, if any. */
  module?: string;
  routes?: { method?: string; prefix: string; key: string }[];
  actions?: { kind: string; key: string }[];
}

/** The 402 body of a refused change: what, why, next, and what would unlock it. */
export interface UpgradeRequiredBody {
  error: UiErrorShape & { code: EntitlementRefusal };
  upgrade: { needs: PlanId; key: string; title: string; unlocks: string };
}

/** A limit and its use, for Settings and the at-limit answer. */
export interface LimitUse {
  key: string;
  limit: number | null;
  used: number;
}

/** state.ext.licensing and GET /api/licensing. */
export interface LicensingState {
  entitlements: OrgEntitlements;
  effective: EffectiveEntitlements;
  limits: LimitUse[];
  /** True on the Node hub's own plan: "Local hub: every feature on". */
  local: boolean;
  /** Plan id from installed licence or state. */
  plan?: PlanId;
  /** Expiry timestamp or string from installed licence or state. */
  expiry?: number | string | null;
  /** Installed offline licence info, if any. */
  licence?: { org: string; plan: PlanId; expiry: number | string } | null;
}

/** POST /api/licensing/ask: "Ask an owner to upgrade" for one key (notifies owners). */
export interface LicensingAskRequest {
  key: string;
  note?: string;
}

// ---------------------------------------------------------------------------
// Billing (control plane only; never on a node, never on the local hub)
// ---------------------------------------------------------------------------

/**
 * Machines are billed as a licensed quantity (ADR 0051 decision 1, option B): raised with
 * proration when a machine is added, corrected at each renewal from the machines that connected
 * that period. No usage meters.
 */
export const MACHINE_BILLING = 'licensed-true-up' as const;
/** Trial length when a Checkout offers one: a setting the owner confirms. */
export const DEFAULT_TRIAL_DAYS = 14;

/** The Stripe subscription statuses, as Stripe names them. */
export type StripeSubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'paused';

/** The Stripe events the billing webhook handles. */
export const BILLING_STRIPE_EVENTS: readonly string[] = deepFreeze([
  'checkout.session.completed',
  'customer.subscription.created',
  'customer.subscription.updated',
  'customer.subscription.deleted',
  'customer.subscription.trial_will_end',
  'invoice.paid',
  'invoice.payment_failed',
  'invoice.payment_action_required',
]);

/** POST /api/billing/checkout */
export interface BillingCheckoutRequest {
  plan: 'team' | 'enterprise';
  /**
   * Machines to start with (Team); defaults to the machines connected now, at least 1. The
   * server bills the larger of this and the machines connected now, so a request cannot buy
   * fewer seats than are already in use.
   */
  machines?: number;
}

/** POST /api/billing/checkout and POST /api/billing/portal answer a URL to open. */
export interface BillingRedirect {
  url: string;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  amountDue: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  periodStart: Ts;
  periodEnd: Ts;
  url: string | null;
}

/** GET /api/billing: Settings, Plan and billing. Amounts in the currency's minor unit. */
export interface BillingSummary {
  orgId: Id;
  plan: PlanId;
  status: EntitlementStatus;
  source: EntitlementSource;
  periodStart: Ts | null;
  periodEnd: Ts | null;
  trialEndsAt: Ts | null;
  /** Distinct machines connected this period: the number that will be billed. */
  machinesThisPeriod: number;
  /** The subscription quantity now. */
  billedQuantity: number;
  people: number;
  limits: LimitUse[];
  invoices: BillingInvoice[];
  /** Set when status is 'grace' or 'lapsed': what failed, why, what to do. */
  problem?: UiErrorShape;
}

// ---------------------------------------------------------------------------
// The checks: pure, shared by the hub, the control plane and the console
// ---------------------------------------------------------------------------

type PlanTable = Readonly<Record<PlanId, Plan>>;

const ENTITLEMENT_KEYS: ReadonlySet<string> = new Set(ENTITLEMENTS.map((e) => e.key));
const FEATURE_KEYS: ReadonlySet<string> = new Set(ENTITLEMENTS.filter((e) => e.kind === 'feature').map((e) => e.key));
const LIMIT_KEYS: ReadonlySet<string> = new Set(ENTITLEMENTS.filter((e) => e.kind === 'limit').map((e) => e.key));
const EVERY_PLAN_SET: ReadonlySet<string> = new Set(EVERY_PLAN_FEATURES);

export function isEntitlementKey(key: unknown): key is string {
  return typeof key === 'string' && ENTITLEMENT_KEYS.has(key);
}

export function isPlanId(v: unknown): v is PlanId {
  return typeof v === 'string' && (PLAN_IDS as readonly string[]).includes(v);
}

/** The entitlements of the Node hub: plan `local` (every feature on), or a sold plan for tests. */
export function localEntitlements(plan: PlanId = 'local', orgId: Id = 'local'): OrgEntitlements {
  return { orgId, plan, overrides: {}, source: 'local', status: 'active', validUntil: null, rev: 1 };
}

/**
 * What an org has now: its plan's features and limits with the org's overrides applied.
 *
 * - `lapsed` (or a contract, design-partner or trial source past `validUntil`, when `now` is
 *   given) falls back to Solo: features above it turn read-only, nothing is deleted.
 * - An override never removes a feature every plan includes (safety, trust and the onboarding
 *   path are never paywalled), and unknown keys are ignored.
 * - `plans` lets the control plane pass the per-deployment plan contents it keeps as data.
 */
export function effective(org: OrgEntitlements, opts: { now?: Ts; plans?: PlanTable } = {}): EffectiveEntitlements {
  const plans = opts.plans ?? PLANS;
  const timed = org.source === 'contract' || org.source === 'design-partner' || org.source === 'trial' || org.source === 'licence';
  const expired = timed && org.validUntil !== null && opts.now !== undefined && opts.now > org.validUntil;
  const status: EntitlementStatus = expired ? 'lapsed' : org.status;
  const plan: PlanId = isPlanId(org.plan) ? org.plan : 'solo';
  const base = status === 'lapsed' ? plans.solo : (plans[plan] ?? plans.solo);
  const on = new Set(base.features.filter((k) => FEATURE_KEYS.has(k)));
  const o = org.overrides ?? {};
  if (status !== 'lapsed') for (const k of o.add ?? []) if (FEATURE_KEYS.has(k)) on.add(k);
  for (const k of o.remove ?? []) if (!EVERY_PLAN_SET.has(k)) on.delete(k);
  for (const k of EVERY_PLAN_FEATURES) on.add(k);
  const limits: Record<string, number | null> = {};
  for (const k of LIMIT_KEYS) {
    const v = base.limits[k];
    limits[k] = v === undefined ? 0 : v;
  }
  if (status !== 'lapsed') {
    for (const [k, v] of Object.entries(o.limits ?? {})) {
      if (LIMIT_KEYS.has(k) && (v === null || (typeof v === 'number' && Number.isInteger(v) && v >= 0))) limits[k] = v;
    }
  }
  return { plan, status, features: ENTITLEMENTS.filter((e) => on.has(e.key)).map((e) => e.key), limits, rev: org.rev };
}

/** The cheapest sold plan that includes a feature, or that allows `need` of a limit. */
export function cheapestPlanFor(key: string, need = 1, plans: PlanTable = PLANS): Exclude<PlanId, 'local'> {
  for (const id of SOLD_PLAN_IDS) {
    const p = plans[id];
    if (!p) continue;
    if (LIMIT_KEYS.has(key)) {
      const v = p.limits[key];
      if (v === null || (v !== undefined && v >= need)) return id;
    } else if (p.features.includes(key)) return id;
  }
  return 'enterprise';
}

type Refusal = Extract<EntitlementCheck, { ok: false }>;

function refusal(key: string, reason: EntitlementRefusal, needs: PlanId): Refusal {
  const e = entitlementByKey(key);
  return { ok: false, reason, key, needs, title: e?.title ?? key, unlocks: e?.unlocks ?? 'This feature is not in the plan catalogue.' };
}

/**
 * May the org use a feature? A key outside the catalogue is refused (fail closed: the catalogue
 * test keeps every declaration to known keys). For a limit key: is the limit above zero.
 */
export function entitled(eff: EffectiveEntitlements, key: string, plans: PlanTable = PLANS): EntitlementCheck {
  if (!ENTITLEMENT_KEYS.has(key)) return refusal(key, 'plan', 'enterprise');
  if (LIMIT_KEYS.has(key)) return withinLimit(eff, key, 0, 1, plans);
  if (eff.features.includes(key)) return { ok: true };
  if (eff.status === 'lapsed' && plans[eff.plan]?.features.includes(key)) return refusal(key, 'lapsed', eff.plan);
  return refusal(key, 'plan', cheapestPlanFor(key, 1, plans));
}

/** How many more a limit allows: null is unlimited, never below 0. An unknown key allows none. */
export function limitLeft(eff: EffectiveEntitlements, key: string, used: number): number | null {
  if (!LIMIT_KEYS.has(key)) return 0;
  const l = eff.limits[key];
  if (l === null) return null;
  if (l === undefined) return 0;
  return Math.max(0, l - Math.max(0, used));
}

/** May `adding` more join a limit that has `used`? Refused with the plan that allows it. */
export function withinLimit(eff: EffectiveEntitlements, key: string, used: number, adding = 1, plans: PlanTable = PLANS): EntitlementCheck {
  if (!LIMIT_KEYS.has(key)) return refusal(key, 'limit', 'enterprise');
  const left = limitLeft(eff, key, used);
  if (left === null || left >= adding) return { ok: true };
  const need = Math.max(0, used) + adding;
  if (eff.status === 'lapsed') {
    const own = plans[eff.plan]?.limits[key];
    if (own === null || (own !== undefined && own >= need)) return refusal(key, 'lapsed', eff.plan);
  }
  return refusal(key, 'limit', cheapestPlanFor(key, need, plans));
}

/** The console's view of one key (`useEntitlement`). `used` matters for limits only. */
export function entitlementUiState(eff: EffectiveEntitlements, key: string, used = 0): EntitlementUiState {
  const c = LIMIT_KEYS.has(key) ? withinLimit(eff, key, used) : entitled(eff, key);
  if (c.ok) return 'on';
  return c.reason === 'lapsed' ? 'lapsed' : c.reason === 'limit' ? 'at-limit' : 'locked';
}

/** "1 machine", "20 missions a month", "7 days": a limit in words. */
export function limitPhrase(key: string, n: number | null): string {
  const e = entitlementByKey(key);
  const title = (e?.title ?? key).toLowerCase();
  if (n === null) return `unlimited ${title}`;
  switch (e?.unit) {
    case 'machines':
      return `${n} ${n === 1 ? 'machine' : 'machines'}`;
    case 'people':
      return `${n} ${n === 1 ? 'person' : 'people'}`;
    case 'projects':
      return `${n} ${n === 1 ? 'project' : 'projects'}`;
    case 'days':
      return `${n} ${n === 1 ? 'day' : 'days'}`;
    case 'per-month':
      return key === 'limit.missions.monthly' ? `${n} ${n === 1 ? 'mission' : 'missions'} a month` : `${n} ${title}`;
    case 'per-day':
      return `${n} a day`;
    default:
      return `${n} ${title}`;
  }
}

/**
 * The 402 body of a refused change: what failed, why, what to do, and the plan that unlocks it.
 * Pass `copy` to say more precisely what was refused ("This machine was not added.").
 */
export function upgradeRequired(eff: EffectiveEntitlements, check: Refusal, copy: Partial<Pick<UiErrorShape, 'what' | 'why' | 'next'>> = {}, plans: PlanTable = PLANS): UpgradeRequiredBody {
  const current = plans[eff.plan]?.title ?? eff.plan;
  const needs = plans[check.needs]?.title ?? check.needs;
  let what = 'This change was not made.';
  let why: string;
  let next: string;
  if (check.reason === 'lapsed') {
    why = `The ${current} subscription lapsed, so ${check.title.toLowerCase()} is read-only until it is restored.`;
    next = 'Restore the subscription in Settings, Plan and billing. Nothing was deleted.';
  } else if (check.reason === 'limit') {
    const covering = eff.status === 'lapsed' ? (plans.solo?.title ?? 'Solo') : current;
    what = 'This was not added.';
    why = `${covering} covers ${limitPhrase(check.key, eff.limits[check.key] ?? 0)}.`;
    next = `Upgrade to ${needs}, or remove one to make room.`;
  } else {
    why = `${check.title} is on the ${needs} plan, and this org is on ${current}.`;
    next = `Upgrade to ${needs} in Settings, Plan and billing, or ask an owner to upgrade.`;
  }
  return {
    error: { what: copy.what ?? what, why: copy.why ?? why, next: copy.next ?? next, code: check.reason },
    upgrade: { needs: check.needs, key: check.key, title: check.title, unlocks: check.unlocks },
  };
}

// ---------------------------------------------------------------------------
// Module declarations: which routes and actions need which key
// ---------------------------------------------------------------------------

/** A change: anything but a read. Reads are never gated unless a route names the method. */
export function isChangeMethod(method: string): boolean {
  const m = method.toUpperCase();
  return m !== 'GET' && m !== 'HEAD' && m !== 'OPTIONS';
}

/** True when `path` is `prefix` or below it, on a segment boundary: '/api/teams' never matches '/api/teamsx'. */
export function underPrefix(path: string, prefix: string): boolean {
  const p = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
  return path === p || path.startsWith(`${p}/`);
}

/**
 * The key a declared route needs for this request, or null. A route without `method` covers
 * changes only (POST, PUT, PATCH, DELETE); a route with one covers exactly that method, reads
 * included. The most specific (longest) prefix wins.
 */
export function declaredRouteKey(decl: ModuleEntitlements, method: string, path: string): string | null {
  const m = method.toUpperCase();
  let best: { len: number; key: string } | null = null;
  for (const r of decl.routes ?? []) {
    if (r.method ? r.method.toUpperCase() !== m : !isChangeMethod(m)) continue;
    if (!underPrefix(path, r.prefix)) continue;
    if (!best || r.prefix.length > best.len) best = { len: r.prefix.length, key: r.key };
  }
  return best ? best.key : null;
}

/** The key a declared action kind needs, or null. */
export function declaredActionKey(decl: ModuleEntitlements, kind: string): string | null {
  return decl.actions?.find((a) => a.kind === kind)?.key ?? null;
}

/** Every key a declaration names, for the catalogue test. */
export function declaredKeys(decl: ModuleEntitlements): string[] {
  return [...new Set([...(decl.module ? [decl.module] : []), ...(decl.routes ?? []).map((r) => r.key), ...(decl.actions ?? []).map((a) => a.key)])];
}
